/**
 * operationalModes.js — ESCALA OPERACIONAL do painel, por modo (claro/escuro). DADO, não lógica.
 *
 * É o "balcão" do Admin: fundo, superfície, texto, divisor, estados, tints de chip, cinzas e
 * sombras. É GLOBAL — igual para os 13 temas. O tema do nicho NÃO mexe aqui: ele só contribui o
 * ACENTO (primária, CTA, drawer) por modo — ver `resolveThemeTokens.js` e
 * docs/ANALISE-TEMAS-MARCA-VS-OPERACAO.md.
 *
 * Chaves = nomes das CSS vars (mesmo vocabulário das Views). Quem escreve este arquivo:
 *   - o Claude Design, em `design-package/src/modes/operational.js`, trazido pelo `import:design`;
 *   - o `build:design` devolve este conteúdo ao pacote a cada envio (Admin = fonte da verdade).
 *
 * Desenhada pelo CD no ciclo "painel em dois modos" (09/2026, rodadas 1–3): carbono neutro no escuro,
 * profundidade por degrau + fio, estados que servem também como preenchimento no MUI legado, aviso
 * âmbar. Regras medidas no import: contraste ≥ 4,5:1 e estados a ΔE ≥ 12 entre si. O racional
 * completo está no cabeçalho de `design-package/src/modes/operational.js`.
 */
export const operationalModes = {
  light: {
    '--bg-page': '#F3F4F6',
    '--surface': '#FFFFFF',
    '--text-primary': '#15171C',
    '--text-secondary': '#596070',
    '--text-disabled': 'rgba(21, 23, 28, 0.40)',
    '--divider': '#E3E6EB',
    '--color-success': '#2C7A26',
    '--color-warning': '#8E6100',
    '--color-error': '#C32A1B',
    '--color-info': '#1663C4',
    '--shadow-card': '0 1px 2px rgba(16,20,28,0.06), 0 1px 1px rgba(16,20,28,0.04)',
    '--shadow-hover': '0 12px 32px rgba(16,20,28,0.14), 0 2px 6px rgba(16,20,28,0.06)',
    '--tint-success-bg': '#E9F6E6',
    '--tint-success-border': '#C5E4BE',
    '--tint-success-fg': '#1D6316',
    '--tint-success-sub': '#2F7328',
    '--tint-warning-bg': '#FCF3E2',
    '--tint-warning-border': '#F0DCB2',
    '--tint-warning-fg': '#7A5200',
    '--tint-warning-sub': '#8F6412',
    '--tint-error-bg': '#FCEDEB',
    '--tint-error-border': '#F3C9C3',
    '--tint-error-fg': '#9C2317',
    '--tint-error-sub': '#B5493C',
    '--tint-info-bg': '#EAF2FC',
    '--tint-info-border': '#CBDEF5',
    '--tint-info-fg': '#14549E',
    '--tint-info-sub': '#2C6BB5',
    '--tint-neutral-bg': '#F4F6F8',
    '--tint-neutral-border': '#E5E8EC',
    '--tint-neutral-fg': '#4E5663',
    '--tint-neutral-sub': '#666E7D',
    '--slate-100': '#F4F6F8',
    '--slate-200': '#E9ECF0',
    '--slate-300': '#D3D8DF',
    '--slate-400': '#A9B0BC',
    '--slate-500': '#7C8494',
    '--slate-600': '#545C6B',
    '--slate-700': '#343B47',
  },
  dark: {
    '--bg-page': '#0F0F10',
    '--surface': '#17181A',
    '--text-primary': '#ECECEE',
    '--text-secondary': '#A0A2A8',
    '--text-disabled': 'rgba(236, 236, 238, 0.36)',
    '--divider': '#262729',
    '--color-success': '#6FD160',
    '--color-warning': '#F3B45C',
    '--color-error': '#F76F61',
    '--color-info': '#63A9F2',
    '--shadow-card': '0 1px 2px rgba(0,0,0,0.55)',
    '--shadow-hover': '0 18px 44px rgba(0,0,0,0.72), 0 2px 8px rgba(0,0,0,0.55)',
    '--tint-success-bg': '#16240F',
    '--tint-success-border': '#2A4620',
    '--tint-success-fg': '#8FD97F',
    '--tint-success-sub': '#6BB35C',
    '--tint-warning-bg': '#241E0E',
    '--tint-warning-border': '#4A3C17',
    '--tint-warning-fg': '#F0C37B',
    '--tint-warning-sub': '#C99F58',
    '--tint-error-bg': '#261613',
    '--tint-error-border': '#4E2823',
    '--tint-error-fg': '#F79A8E',
    '--tint-error-sub': '#CC7468',
    '--tint-info-bg': '#121F2C',
    '--tint-info-border': '#223A53',
    '--tint-info-fg': '#86C1F3',
    '--tint-info-sub': '#689FCB',
    '--tint-neutral-bg': '#1D1E21',
    '--tint-neutral-border': '#2C2D31',
    '--tint-neutral-fg': '#B4B6BC',
    '--tint-neutral-sub': '#8B8D94',
    '--slate-100': '#1C1D20',
    '--slate-200': '#26272A',
    '--slate-300': '#35363A',
    '--slate-400': '#4E5054',
    '--slate-500': '#75777D',
    '--slate-600': '#A0A2A8',
    '--slate-700': '#C9CBD0',
  },
};

/** Nomes das vars da escala — iguais nos dois modos (o teste de arquitetura confere). */
export const OPERATIONAL_VARS = Object.keys(operationalModes.light);
